import './App.css';
import { Dashboard } from 'components/dashboard';
import { ContractPage } from 'components/contract';
import { Settings } from 'components/settings';
import { Routes, Route, Link } from 'react-router-dom';
import { auth, useAuthState } from './firebaseStore';
import { useState } from 'react';
import { Button } from 'components/button';
import { Login } from 'components/login';
import { TranslationsPage } from 'components/translationsPage';
import { ContractClausesPage } from 'components/contract/contractClauses';
import { ContractLanguagesPage } from 'components/contractLanguagesPage';
import { ContractLanguagePage } from 'components/contractLanguagePage';
import { ContractTranslationPage } from 'components/contractTranslationPage';
import * as Sentry from '@sentry/react';
import { UsersPage } from 'components/usersPage';
import { CollectionProvider } from 'components/settings/collectionContext';
import {
    accountConverter,
    ACCOUNT_COLLECTION_NAME,
    userProfileCoverter,
    USER_PROFILE_COLLECTION_NAME,
} from 'models';
import { where } from 'firebase/firestore';
import { useUserProfile } from 'components/settings/userProfileContext';
import { ProfileLoading, RegisterMe } from 'components/login/login';
import { CommitsPage } from 'components/commitsPage';
import { ContractCommitsPage } from 'components/contractCommitsPage';
import { AccountPage } from 'components/accountPage';
import { UploadPage } from 'components/uploadPage';

function App() {
    const [user, pending] = useAuthState();

    if (pending) {
        return (
            <div
                style={{
                    width: '100vw',
                    height: '100vh',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                Loading
            </div>
        );
    }
    if (!user) {
        return <Login />;
    }
    if (user.email) {
        Sentry.setUser({ email: user.email });
    }
    return (
        <CollectionProvider
            collectionName={USER_PROFILE_COLLECTION_NAME}
            converter={userProfileCoverter}
            constraints={[where('email', '==', user?.email || '')]}
        >
            <CollectionProvider
                collectionName={ACCOUNT_COLLECTION_NAME}
                converter={accountConverter}
            >
                <AppRoutes />
            </CollectionProvider>
        </CollectionProvider>
    );
}

const AppRoutes = () => {
    const [me, userProfilePending] = useUserProfile();
    if (userProfilePending) {
        return <ProfileLoading />;
    }
    if (!me) {
        return <RegisterMe />;
    }
    return (
        <div className="App">
            <Header />
            <Routes>
                <Route path="/" element={<Dashboard />} />
                <Route path="/contract" element={<ContractPage />} />
                <Route path="/contract/:contractId" element={<ContractPage />} />
                <Route path="/contract-clauses/:contractId" element={<ContractClausesPage />} />
                <Route path="/contract-languages/:contractId" element={<ContractLanguagesPage />} />
                <Route path="/contract/:contractId/commits" element={<ContractCommitsPage />} />
                <Route
                    path="/contract-languages/:contractId/language/:languageId"
                    element={<ContractLanguagePage />}
                />
                <Route
                    path="/contract-languages/:contractId/translation/:languageId"
                    element={<ContractTranslationPage />}
                />
                <Route path="/settings" element={<Settings />} />
                <Route path="/settings/:recordType" element={<Settings />} />
                <Route path="/settings/:recordType/commits" element={<CommitsPage />} />
                <Route path="/users/*" element={<UsersPage />} />
                <Route path="/account" element={<AccountPage />} />
                <Route path="/uploads" element={<UploadPage />} />
                <Route path="/uploads/:uploadId" element={<UploadPage />} />
                <Route path="/translations/:languageId" element={<TranslationsPage />} />
            </Routes>
        </div>
    );
};

const Impersonate = () => {
    const [value, setValue] = useState(localStorage.getItem('impersonatedTenant') || '');
    if (!auth.currentUser) {
        return null;
    }
    if (!auth.currentUser.email?.endsWith('terminos.io')) {
        return null;
    }
    return (
        <div style={{ marginRight: '15px', display: 'flex' }}>
            <input
                value={value}
                onChange={(e) => setValue(e.target.value)}
                onKeyDown={(e) => {
                    if (e.key == 'Enter') {
                        if (value === localStorage.getItem('impersonatedTenant')) {
                            return;
                        }
                        localStorage.setItem('impersonatedTenant', value);
                        setValue(value);
                        window.location.reload();
                    }
                }}
            />
            <button
                className="sign-out"
                onClick={() => {
                    if (value === localStorage.getItem('impersonatedTenant')) {
                        return;
                    }
                    localStorage.setItem('impersonatedTenant', value);
                    setValue(value);
                    window.location.reload();
                }}
            >
                Demo
            </button>
        </div>
    );
};

function SignOut() {
    return (
        auth.currentUser && (
            <Button
                theme="soft"
                style={{ width: 'auto', fontSize: '20px' }}
                onClick={() => {
                    console.warn('signOut');
                    auth.signOut();
                }}
            >
                Sign Out
            </Button>
        )
    );
}

const headerLinkStyle = { textDecoration: 'unset', color: 'unset' };
const Header = () => {
    const [me] = useUserProfile();
    return (
        <header className="App-header">
            <div
                className="App-header-section"
                style={{
                    position: 'absolute',
                    width: 'calc(100% - 20px)',
                    left: '20px',
                    justifyContent: 'left',
                }}
            >
                <Link to="/" style={{ zIndex: 111 }}>
                    <img src="/logo.png" style={{ height: '40px' }} alt="Terminos logo" />
                </Link>
            </div>
            <div className="App-header-section" style={{ zIndex: '1' }}>
                <div className="App-header-link">
                    <Link to="/" style={headerLinkStyle}>
                        Dashboard
                    </Link>
                </div>
                <div className="App-header-link">
                    <Link to="/settings" style={headerLinkStyle}>
                        Settings
                    </Link>
                </div>
                <div className="App-header-link">
                    <Link to="/settings/translations" style={headerLinkStyle}>
                        Translations
                    </Link>
                </div>
                {me?.admin() && (
                    <div className="App-header-link">
                        <Link to="/users" style={headerLinkStyle}>
                            Users
                        </Link>
                    </div>
                )}
                <div className="App-header-link">
                    <Link to="/account" style={headerLinkStyle}>
                        Account
                    </Link>
                </div>
            </div>
            <div
                style={{
                    position: 'absolute',
                    width: '100%',
                    justifyContent: 'right',
                    marginRight: '40px',
                }}
                className="App-header-section"
            >
                <Impersonate />
                <SignOut />
            </div>
        </header>
    );
};

export default App;
